// ts-rest contract for the Desk Internal Suitability Assessment (WP-G).
//
// The IM fills the Dowgate "Internal Suitability Assessment" form AFTER the
// client completes the Fact Find (internal-use only). The endpoints: a `get`
// (returns the client's current assessment, or a PREFILLED blank draft seeded
// from the Client + Fact Find when none exists yet), `save` (upsert the working
// draft), `signOff` (the responsible IM locks the content into a version) and
// `openAmendmentRound` (the next round, SEP-243 sections 30, 33 and 35).
//
// Per ADR-0003 the DTO is a real `z.object` of the stored raw inputs plus a
// `derived` block the controller computes via `computeSuitabilityAssessment`
// (the web form recomputes the same derivations live as the IM types, so the
// `derived` block is a convenience mirror, not the source of truth).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';
// The six risk profiles have ONE definition for the whole wire. The enquiry
// intent reads the same list, so the client's intention and the IM's
// recommendation are directly comparable (SEP-243 section 24).
import { riskProfileSchema } from '../risk-profile';
// SEP-243 section 26 - the append-only override register. The calculated value
// stays beside the override, and the reason is mandatory.
import {
    suitabilityCalculationKeySchema,
    suitabilityCalculationOverrideSchema,
} from '../suitability-calculation-overrides';
// SEP-243 sections 28 and 29 - which declarations the data supports.
import { suitabilityDeclarationSupportSchema } from '../suitability-declaration-support';
// SEP-243 sections 30, 33 and 35 - the eight states of the review, and the pure
// rules that read them.
import { suitabilityLifecycleSchema } from '../suitability-assessment-lifecycle';

const c = initContract();

import { errorBody } from '../schemas/common';

// --- enums (snake_case domain values, camelCase keys) ---
// The LEGACY two-value status. SEP-243 slice 15 drops the column, and
// `lifecycle` below is the truth. It stays on the wire for one release so that
// a caller of the previous release still reads a true value.
const statusSchema = z.enum(['draft', 'completed']);
const clientTypeSchema = z.enum(['individual', 'entity']);
const classificationSchema = z.enum(['retail', 'professional', 'hnw']);
// The entity form's classification axis (Retail vs Elective Professional Client).
const entityClassificationSchema = z.enum(['retail', 'epc']);
const employmentStatusSchema = z.enum([
    'employed',
    'self_employed',
    'retired',
    'director',
    // The "Not working" box of the ISA v3 form (SEP-293).
    'not_working',
    'other',
]);
const serviceRecommendationSchema = z.enum(['dsc', 'adv', 'adv_managed']);

const complianceResultSchema = z.enum(['approved', 'rejected', 'more_information_required']);

/** One regular withdrawal of the ISA v3 form, section 5 (SEP-293). */
export const regularWithdrawalSchema = z.object({
    portfolio: z.enum(['isa', 'gia', 'sipp']),
    amount: z.number().nullable(),
    frequency: z.enum(['monthly', 'quarterly', 'half_yearly', 'annually']).nullable(),
});

/** One one-off capital withdrawal of the ISA v3 form, section 5 (SEP-293). */
export const oneOffWithdrawalSchema = z.object({
    amount: z.number().nullable(),
    expectedDate: z.string().nullable(),
    purpose: z.string().nullable(),
});

/** The measures A to M of the ISA v3 form (SEP-293), for a `v3` row. */
export const internalSuitabilityMeasuresSchema = z.object({
    A: z.boolean().nullable(),
    B: z.number(),
    C: z.number(),
    D: z.number(),
    E: z.number(),
    F: z.number().nullable(),
    fBand: z.enum(['very_low', 'low', 'medium', 'high', 'very_high']).nullable(),
    G: z.number(),
    H: z.number().nullable(),
    I: z.number(),
    J: z.number().nullable(),
    jRating: z
        .enum(['highly_liquid', 'adequately_liquid', 'potentially_constrained', 'liquidity_risk'])
        .nullable(),
    K: z.boolean(),
    L: z.boolean(),
    M: z.boolean(),
    totalAssets: z.number(),
    totalLiabilities: z.number(),
    illiquidPensions: z.number(),
    cash: z.number(),
    /** The age at the review date and the pension access age that A reads. */
    ageAtReview: z.number().nullable(),
    pensionAccessAge: z.number().nullable(),
});
export type InternalSuitabilityMeasuresDto = z.infer<typeof internalSuitabilityMeasuresSchema>;

// --- the raw, IM-entered fields (what gets stored + what `save` collects) ---
// Every field is nullable: a draft is partial by nature.
export const suitabilityAssessmentFieldsSchema = z.object({
    clientTitle: z.string().nullable(),
    clientForenames: z.string().nullable(),
    clientSurname: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    accountNumber: z.string().nullable(),
    // Entity variant only (null for individuals).
    entityName: z.string().nullable(),
    entityClassification: entityClassificationSchema.nullable(),
    representativeClassification: entityClassificationSchema.nullable(),
    investmentManagerName: z.string().nullable(),
    reviewDate: z.string().nullable(),
    lastReviewDate: z.string().nullable(),

    isBusinessOwner: z.boolean().nullable(),
    isFinancialServicesProfessional: z.boolean().nullable(),
    clientClassification: classificationSchema.nullable(),
    otherRelevantExperience: z.string().nullable(),

    currentVulnerabilityScore: z.number().nullable(),
    newVulnerabilityScore: z.number().nullable(),
    vulnerabilityChangeComments: z.string().nullable(),

    dowgatePortfolioExclSippIsa: z.number().nullable(),
    dowgatePortfolioExclSippNonIsa: z.number().nullable(),
    otherDowgateAssets: z.number().nullable(),
    cashSavingsIsa: z.number().nullable(),
    cashSavingsNonIsa: z.number().nullable(),
    mainPropertyValue: z.number().nullable(),
    otherPropertyValue: z.number().nullable(),
    personalPensionDowgateSipp: z.number().nullable(),
    personalPensionOther: z.number().nullable(),
    otherPensionAssets: z.number().nullable(),
    businessInterests: z.number().nullable(),
    otherMaterialAssetsIsa: z.number().nullable(),
    otherMaterialAssetsNonIsa: z.number().nullable(),
    otherMaterialAssetsChattels: z.number().nullable(),
    mortgageOutstanding: z.number().nullable(),
    anticipatedMortgagePayOffDate: z.string().nullable(),
    otherMaterialDebts: z.number().nullable(),

    employmentStatus: employmentStatusSchema.nullable(),
    employmentStatusOther: z.string().nullable(),
    employmentIncome: z.number().nullable(),
    dowgateTaxableIncome: z.number().nullable(),
    dowgateIsaIncome: z.number().nullable(),
    dowgateSippIncome: z.number().nullable(),
    dowgateOtherIncome: z.number().nullable(),
    statePensionIncome: z.number().nullable(),
    otherPensionIncome: z.number().nullable(),
    otherInvestmentIncome: z.number().nullable(),
    allOtherIncome: z.number().nullable(),
    annualMortgageRentExpenditure: z.number().nullable(),
    annualEssentialExpenditure: z.number().nullable(),
    annualDiscretionaryExpenditure: z.number().nullable(),
    annualOtherExpenditure: z.number().nullable(),

    totalInvestableAssetsOverride: z.number().nullable(),
    capitalRequirements: z.number().nullable(),
    totalPortfoliosAtDowgate: z.number().nullable(),
    illiquidAssets: z.number().nullable(),
    totalClientWealthOverride: z.number().nullable(),
    incomeYieldAssumption: z.number().nullable(),

    liquidityComment: z.string().nullable(),
    serviceRecommendation: serviceRecommendationSchema.nullable(),
    serviceRationale: z.string().nullable(),
    riskProfileRecommendation: riskProfileSchema.nullable(),
    riskProfileRationale: z.string().nullable(),
    strategyRecommendation: z.string().nullable(),
    strategyRationale: z.string().nullable(),

    declInformationCollected: z.boolean().nullable(),
    declCircumstancesAssessed: z.boolean().nullable(),
    declStrategyRecommended: z.boolean().nullable(),
    declRiskWarningsProvided: z.boolean().nullable(),
    declInvestmentRestrictionsConsidered: z.boolean().nullable(),
    declCostChargesDisclosed: z.boolean().nullable(),
    signatureName: z.string().nullable(),
    signatureDate: z.string().nullable(),
    peerReviewName: z.string().nullable(),
    peerReviewDate: z.string().nullable(),
    peerReviewComments: z.string().nullable(),
    complianceApprovalName: z.string().nullable(),
    complianceApprovalDate: z.string().nullable(),
    complianceApprovalResult: complianceResultSchema.nullable(),
    complianceApprovalComments: z.string().nullable(),

    // --- The ISA v3 form (SEP-293 sections 7 and 8) ---
    // Optional: a signed-off version of the past holds none of these.
    /** The arithmetic of the row. Read-only: the save Action sets it. */
    calculationVersion: z.enum(['v2', 'v3']).nullable().optional(),
    pensionAccessAgeBasis: z.enum(['55', '57', 'protected']).nullable().optional(),
    protectedPensionAge: z.number().int().nullable().optional(),
    otherInvestmentsExternal: z.number().nullable().optional(),
    giaOwnershipPct: z.number().nullable().optional(),
    isaOwnershipPct: z.number().nullable().optional(),
    externalInvestmentsOwnershipPct: z.number().nullable().optional(),
    cashOwnershipPct: z.number().nullable().optional(),
    mainPropertyOwnershipPct: z.number().nullable().optional(),
    otherPropertyOwnershipPct: z.number().nullable().optional(),
    businessEisOwnershipPct: z.number().nullable().optional(),
    chattelsOwnershipPct: z.number().nullable().optional(),
    mortgageOwnershipPct: z.number().nullable().optional(),
    regularWithdrawalSchedule: z.array(regularWithdrawalSchema).nullable().optional(),
    oneOffWithdrawals: z.array(oneOffWithdrawalSchema).nullable().optional(),
    strategyAssetClasses: z.array(z.string()).nullable().optional(),
    cflCrossCheckConsistent: z.boolean().nullable().optional(),
    cflCrossCheckComment: z.string().nullable().optional(),
    cashCrossCheckConsistent: z.boolean().nullable().optional(),
    cashCrossCheckComment: z.string().nullable().optional(),
    portfolioPositioningConsistent: z.enum(['yes', 'no', 'na']).nullable().optional(),
    portfolioPositioningComment: z.string().nullable().optional(),
    rebalancingAction: z.string().nullable().optional(),
    rebalancingActionDate: z.string().nullable().optional(),
    suitabilityRefreshId: z.string().nullable().optional(),
    // The enhanced rationale of a Red Portfolio Reliance check (SEP-293
    // section 8), in the Conclusion.
    relianceFundingSource: z.enum(['natural_yield', 'capital', 'both']).nullable().optional(),
    relianceSustainableWithdrawalRatePct: z.number().nullable().optional(),
    relianceStressTestNote: z.string().nullable().optional(),
    relianceCashBufferAmount: z.number().nullable().optional(),
    relianceCashBufferLocation: z.enum(['inside', 'outside', 'both']).nullable().optional(),
    relianceCashBufferMonths: z.number().nullable().optional(),
    relianceRiskLevelJustification: z.string().nullable().optional(),
    relianceLowerRiskAlternatives: z.string().nullable().optional(),
    relianceClientUnderstands: z.boolean().nullable().optional(),
    relianceDiscussedOn: z.string().nullable().optional(),
    relianceReviewFrequencyIncrease: z.boolean().nullable().optional(),
    relianceReviewFrequencyNote: z.string().nullable().optional(),
});
export type SuitabilityAssessmentFields = z.infer<typeof suitabilityAssessmentFieldsSchema>;

// --- the controller-derived figures (mirror of computeSuitabilityAssessment) ---
export const suitabilityAssessmentDerivedSchema = z.object({
    totalAssets: z.number(),
    totalLiabilities: z.number(),
    netAssets: z.number(),
    totalIncome: z.number(),
    totalOutgoings: z.number(),
    totalInvestableAssets: z.number(),
    capacityForLossPct: z.number(),
    capacityForLossBand: z.enum(['very_low', 'low', 'medium', 'high', 'very_high']),
    capacityForLossBandLabel: z.string(),
    capacityForLossStatus: z.enum(['ok', 'manual_assessment_tia_zero', 'capreq_exceeds_tia']),
    portfolioFallTolerancePct: z.number(),
    portfolioFallStatus: z.enum(['ok', 'no_income_dependency', 'not_computable']),
    totalClientWealth: z.number(),
    totalLiquidityPct: z.number(),
    liquidityRating: z.enum([
        'liquidity_risk',
        'potentially_constrained',
        'adequately_liquid',
        'highly_liquid',
    ]),
    liquidityRatingLabel: z.string(),
    liquidityStatus: z.enum(['ok', 'cannot_compute', 'over_100']),
    liquidityNeedsImComment: z.boolean(),
    nonDowgateIncomeCoversStressedOutgoings: z.boolean(),
    cashCoversSixMonths: z.boolean(),
    cashCoversTwelveMonths: z.boolean(),
});
// Wire-shape mirror of the helper's `SuitabilityAssessmentDerived` (kept local
// to avoid an export-name clash with the helper; structurally identical).
export type SuitabilityAssessmentDerivedDto = z.infer<typeof suitabilityAssessmentDerivedSchema>;

// --- the full assessment DTO ---
export const suitabilityAssessmentDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    /** The person who CREATED the review. It is not the person who signs it off. */
    imUserId: z.string(),
    /** The legacy two-value status. Read `lifecycle` instead. */
    status: statusSchema,
    /** The state of the review (SEP-243 sections 30, 33 and 35). */
    lifecycle: suitabilityLifecycleSchema,
    /**
     * The investment manager who ANSWERS for the review, and the only person
     * who may sign it off. The screen compares it against the signed-in user,
     * so an EA sees no sign-off affordance.
     */
    responsibleImUserId: z.string().nullable(),
    /** Why the present amendment round is open. A first round carries none. */
    amendmentRoundReason: z.string().nullable(),
    clientType: clientTypeSchema,
    fields: suitabilityAssessmentFieldsSchema,
    derived: suitabilityAssessmentDerivedSchema,
    /**
     * The arithmetic of the row (SEP-293). `v2` reads `derived`; `v3` reads
     * `measures` too, which holds the measures A to M of the ISA v3 form.
     */
    calculationVersion: z.enum(['v2', 'v3']),
    measures: internalSuitabilityMeasuresSchema.nullable(),
    createdAt: z.string(),
    completedAt: z.string().nullable(),
});
export type SuitabilityAssessmentDto = z.infer<typeof suitabilityAssessmentDtoSchema>;

// ---------------------------------------------------------------------------
// The signed-off versions (SEP-243 sections 30, 33 and 35)
// ---------------------------------------------------------------------------
//
// One row is one sign-off. The row is immutable: no endpoint updates one and no
// endpoint deletes one. The list travels on every open, newest first, so the
// reader sees that version 1 survived every later round.

export const suitabilityAssessmentVersionSchema = z.object({
    id: z.string(),
    assessmentId: z.string(),
    clientId: z.string(),
    /** Counts from 1 for each assessment. */
    versionNumber: z.number().int(),
    clientType: clientTypeSchema,
    /** The whole form-field block at the instant of the sign-off. */
    fields: suitabilityAssessmentFieldsSchema,
    /** Why the round that produced this version was open. Version 1 carries none. */
    roundReason: z.string().nullable(),
    responsibleImUserId: z.string().nullable(),
    signedOffByUserId: z.string().nullable(),
    /** The name of the person who signed off, for the screen. */
    signedOffByName: z.string().nullable(),
    signedOffAt: z.string(),
});
export type SuitabilityAssessmentVersionDto = z.infer<typeof suitabilityAssessmentVersionSchema>;

// ---------------------------------------------------------------------------
// The pull-through block (SEP-243 sections 12 and 17 to 25)
// ---------------------------------------------------------------------------
//
// The firm's instruction in section 17: the Internal Review must be "largely
// pre-populated from information already held", so the IM exercises judgement
// instead of re-keys facts. The block below is the READ-ONLY half of that. It
// holds every group the ticket lists that is NOT an IM-entered form field.
//
// The block travels on EVERY open of the review, and not only on a review that
// holds no assessment yet. A client correction that lands after the review
// opened must reach the open review, and the read-only panels therefore always
// show the current confirmed value.

// `submitted` joined the set with the submission lock (SEP-257 item 17): a
// submitted fact find is frozen, so the review reads figures that cannot move.
export const factFindSourceKindSchema = z.enum([
    'receipt_locked',
    'approved',
    'submitted',
    'working',
]);

/**
 * Which fact-find version the review reads, and how safe its figures are. The
 * panel prints this, so the IM never guesses whether the numbers are the frozen
 * client answers or a draft that can still move.
 */
export const factFindSourceSchema = z.object({
    versionId: z.string(),
    versionNumber: z.number().int().nullable(),
    status: z.string().nullable(),
    kind: factFindSourceKindSchema,
    receiptLockedAt: z.string().nullable(),
    receiptDoor: z.enum(['staff_upload', 'portal_link']).nullable(),
    legacyUnlocked: z.boolean(),
    /** The count of ACTIVE evidenced amendments folded into the figures. */
    amendmentCount: z.number().int(),
});
export type FactFindSourceDto = z.infer<typeof factFindSourceSchema>;

/**
 * The fact-find risk result (sections 12 and 22). The firm was explicit: do not
 * ask the IM to enter it again. Every value comes from the stored fact find.
 */
export const factFindRiskSummarySchema = z.object({
    band: z.number(),
    bandMaximum: z.number(),
    total: z.number(),
    rawScore: z.number(),
    mandate: z.string(),
    answered: z.number().int(),
    totalQuestions: z.number().int(),
    complete: z.boolean(),
    capacityFlags: z.array(z.string()),
    capacityFlagLabels: z.array(z.string()),
    /** `Low` when the instrument raised a signal, else `Not flagged`. */
    capacityForRisk: z.enum(['Low', 'Not flagged']),
    /**
     * Why each flag fired: the question, the answer and the rule (SEP-257 item
     * 14). Capacity for risk is a FLAG of the questionnaire, and Capacity for
     * Capital Loss is a CALCULATION over the figures of section 5. The panel
     * prints this list so a reader cannot read one as the other.
     */
    capacityFlagExplanations: z.array(
        z.object({
            code: z.string(),
            label: z.string(),
            questionId: z.string(),
            questionName: z.string(),
            answerLabel: z.string().nullable(),
            rule: z.string(),
        }),
    ),
    capacityForLossBand: z.enum(['low', 'medium', 'high']).nullable(),
    capacityForLossBandLabel: z.string().nullable(),
    computedAt: z.string().nullable(),
    /**
     * False when the CURRENT questionnaire does not recognise every stored
     * answer. The figures stay the stored ones, and the panel states that an
     * earlier instrument produced them.
     */
    instrumentMatches: z.boolean(),
});
export type FactFindRiskSummaryDto = z.infer<typeof factFindRiskSummarySchema>;

/** What the new application captured (sections 17 and 23, and item 6). */
export const suitabilityEnquiryIntentSchema = z.object({
    caseId: z.string().nullable(),
    isaSelection: z.enum(['not_asked', 'yes', 'no']).nullable(),
    isaSelectionLabel: z.string().nullable(),
    investmentObjective: z.enum(['income', 'growth', 'balance']).nullable(),
    investmentObjectiveLabel: z.string().nullable(),
    investmentObjectiveExplanation: z.string().nullable(),
    /**
     * The risk profile the CLIENT asked for. It is an indicator, and never a
     * seed of `riskProfileRecommendation` (SEP-243 section 24).
     */
    intendedRiskProfile: riskProfileSchema.nullable(),
    intendedRiskProfileLabel: z.string().nullable(),
    /**
     * Is the client a business owner? The fact find asks it, and its answer
     * governs (SEP-263 decision D5). The case column of the Stage 1 intake is
     * the fallback for a case that the intake raised before that change. The
     * Internal Review PRE-POPULATES from this answer rather than asking again
     * (SEP-243 item 6). `null` is "no source holds an answer", which the form
     * shows as an outstanding item rather than as "no".
     */
    isBusinessOwner: z.boolean().nullable(),
});

/** One asset class of the fact-find experience block (section 17). */
export const investmentExperienceEntrySchema = z.object({
    questionId: z.string(),
    label: z.string(),
    level: z.string().nullable(),
    hasExperience: z.boolean(),
});

/** The knowledge and qualification answers of the fact find (section 17). */
export const factFindKnowledgeSchema = z.object({
    workedInFinance: z.boolean().nullable(),
    holdsFinancialQualification: z.boolean().nullable(),
    investedInAim: z.boolean().nullable(),
    knowledgeableInvestor: z.string().nullable(),
    experienceLength: z.string().nullable(),
    otherProductsExperience: z.string().nullable(),
});

/** One scored answer of the Knowledge and Experience breakdown (SEP-257 item 13). */
export const knowledgeExperienceLineSchema = z.object({
    questionId: z.string(),
    label: z.string(),
    answer: z.string().nullable(),
    points: z.number(),
    maxPoints: z.number(),
    note: z.string().nullable(),
});

/**
 * The Knowledge and Experience score of the locked fact find (SEP-257 item 13).
 *
 * It INFORMS the judgement of the IM. It never decides the client
 * classification, and it never decides suitability. The band is null when the
 * fact find answers no scored question.
 */
export const knowledgeExperienceScoreSchema = z.object({
    total: z.number(),
    maximum: z.number(),
    band: z.enum(['basic', 'informed', 'experienced', 'sophisticated']).nullable(),
    bandLabel: z.string().nullable(),
    lines: z.array(knowledgeExperienceLineSchema),
    /** What the client says about themself, against the band the answers give. */
    selfRated: z.object({
        answer: z.string().nullable(),
        agreesWithBand: z.boolean().nullable(),
    }),
});

/**
 * The classification, from the fact find and from the Compliance record.
 *
 * The record of section 10 carries FOUR values, and the Internal Review field
 * carries three. The two sets are deliberately different, so the block prints
 * the record value as the record holds it, and it maps nothing.
 */
export const classificationRecordValueSchema = z.enum([
    'retail',
    'professional',
    'elective_professional',
    'eligible_counterparty',
]);

export const suitabilityClassificationPullThroughSchema = z.object({
    /** The `client_classification` answer of the fact find, as the client gave it. */
    factFindAnswer: z.string().nullable(),
    /** The Compliance record of section 10, when the case holds one. */
    recordId: z.string().nullable(),
    proposedClassification: classificationRecordValueSchema.nullable(),
    assessmentStatus: z.string().nullable(),
    finalClassification: classificationRecordValueSchema.nullable(),
});

/** The investment restrictions of the fact find (section 17). */
export const suitabilityRestrictionsSchema = z.object({
    categories: z.array(z.string()),
    /**
     * The whole restriction statement, folded into one block of text by
     * `foldRestrictionDetail`. Four categories carry their detail in a question
     * of their own, and the block prints each one under its label (SEP-257 item
     * 16). A panel that read `restrictions_detail` alone dropped them.
     */
    detail: z.string().nullable(),
});

/** One sales-process document the firm gave the client (sections 4 and 27). */
export const suitabilitySalesDocumentSchema = z.object({
    documentCode: z.string(),
    documentLabel: z.string(),
    provided: z.enum(['yes', 'no', 'na']),
    versionLabel: z.string().nullable(),
    dateProvided: z.string().nullable(),
    mandatory: z.boolean(),
    outstanding: z.boolean(),
});

/**
 * Where the provided-document register stands (section 27).
 *
 * `summariseProvidedDocuments` in `../onboarding-document-registers` computes
 * every field, and nothing stores the result. The Internal Review READS the
 * register of the case, and it never asks the IM to fill a second checklist:
 * the ticket is explicit on that point.
 */
export const suitabilitySalesDocumentSummarySchema = z.object({
    total: z.number().int(),
    mandatoryTotal: z.number().int(),
    mandatorySettled: z.number().int(),
    /** The mandatory rows of the register that carry a `no` answer. */
    outstandingMandatory: z.array(z.string()),
    /** The mandatory catalogue items the register does NOT carry at all. */
    missingMandatory: z.array(z.string()),
    /** The catalogue codes the firm has not confirmed. They are not blockers. */
    awaitingFirmConfirmation: z.array(z.string()),
    /** The provided rows that still wait on an acknowledgement they need. */
    missingAcknowledgements: z.array(z.string()),
    allMandatoryProvided: z.boolean(),
    /**
     * TRUE when the persona of the reader may not read the register at all.
     *
     * Every count beside it is then zero and every list is empty, and NONE of
     * them is a fact about the case. Without this flag a withheld register
     * reads as a firm that gave the client nothing, which is a false claim on
     * the wire even where the panel of today happens to hide it. Every sibling
     * block of the pull-through carries the same flag.
     */
    withheld: z.boolean(),
});

/** One evidenced client correction (sections 7 and 17). */
export const suitabilityClientCorrectionSchema = z.object({
    amendmentId: z.string(),
    sectionId: z.string(),
    questionId: z.string(),
    /**
     * The Internal Review field the corrected answer feeds, or null when the
     * answer feeds no field of the review.
     */
    reviewFieldKey: z.string().nullable(),
    originalValue: z.string().nullable(),
    correctedValue: z.string().nullable(),
    reason: z.string(),
    sourceKind: z.string(),
    recordedByName: z.string().nullable(),
    recordedAt: z.string(),
});

/** One flag the review must show (section 17, "relevant flags"). */
export const suitabilityFlagSchema = z.object({
    code: z.string(),
    label: z.string(),
    /** Where the flag came from, so a reader opens the right record. */
    source: z.enum(['fact_find_risk', 'vulnerability', 'fact_find_amendment']),
});

/** The vulnerability position (section 19). */
export const suitabilityVulnerabilitySchema = z.object({
    /** True when the client holds a COMPLETED assessment. */
    exists: z.boolean(),
    assessmentId: z.string().nullable(),
    reference: z.string().nullable(),
    status: z.string().nullable(),
    score: z.number().nullable(),
    band: z.string().nullable(),
    bandLabel: z.string().nullable(),
    completionDate: z.string().nullable(),
    nextReviewDate: z.string().nullable(),
    /** The drivers the fact find flagged, in the words the panel prints. */
    drivers: z.array(z.string()),
    /**
     * False when the stored answers key into no current question. The panel
     * then reads "Not scored", and it never prints a reassuring zero.
     */
    scoreable: z.boolean(),
    // The explicit outcome and the support record of the assessment (SEP-293
    // section 7). The Internal Review holds the vulnerability summary.
    outcome: z
        .enum(['not_vulnerable', 'potentially_vulnerable', 'vulnerable'])
        .nullable()
        .optional(),
    communicationNeeds: z.array(z.string()).optional(),
    outcomeReason: z.string().nullable().optional(),
    supportPlan: z.string().nullable().optional(),
    /** "How the documents were explained" (SEP-318 decision 8). */
    documentExplanation: z.string().nullable().optional(),
});

/**
 * A saved review field that still holds the answer BEFORE the correction.
 *
 * The prefill never overwrites a value the IM entered, so a correction that
 * lands after the IM saved a figure reaches the screen but not that field. This
 * list names each such field, so the IM sees the disagreement and takes the
 * corrected figure on purpose.
 */
export const suitabilitySupersededFieldSchema = z.object({
    /** The Internal Review field key, for example `mainPropertyValue`. */
    fieldKey: z.string(),
    /** The fact-find question the correction moved. */
    questionId: z.string(),
    /** What the saved review holds now. */
    savedValue: z.string().nullable(),
    /** The current confirmed value of the fact find. */
    confirmedValue: z.string().nullable(),
});

/**
 * The pull-through groups the Persona Gate can suppress.
 *
 * The read route admits `desk`, `view` and `comply`, and the sources do not all
 * admit the same set. A suppressed source must never print as an absence: a
 * leadership actor would otherwise read "the client holds no vulnerability
 * assessment" as a fact on a regulated review document. A true value here makes
 * the panel print "Withheld from this persona" instead.
 */
export const suitabilityWithheldSchema = z.object({
    /** The fact-find version: the answers, the figures and the risk result. */
    factFind: z.boolean(),
    /** The evidenced amendments. */
    amendments: z.boolean(),
    /** The onboarding case, which states the intention of the client. */
    enquiry: z.boolean(),
    /** The Compliance classification record. */
    classification: z.boolean(),
    /** The provided-document register. */
    salesDocuments: z.boolean(),
    /** The vulnerability assessment. */
    vulnerability: z.boolean(),
});

/**
 * One fact that the firm already holds, for a field of the form (SEP-325).
 *
 * The form fills the field from it and prints the source beside the field. If
 * the IM types a different value, the form shows a warning.
 */
export const suitabilityKnownFactSchema = z.object({
    /** The Internal Review field key, for example `dateOfBirth`. */
    fieldKey: z.string(),
    /** The value as the form holds it: a date as `YYYY-MM-DD`, a score as digits. */
    value: z.string(),
    /** Where the value came from, in words for the IM, for example `Client application`. */
    source: z.string(),
});
export type SuitabilityKnownFactDto = z.infer<typeof suitabilityKnownFactSchema>;

/** The rationale rule of section 24, as the ontology decides it. */
export const suitabilityRiskRationaleSchema = z.object({
    required: z.boolean(),
    reasons: z.array(z.enum(['differs_from_intent', 'differs_from_fact_find_band'])),
});

/** One clause of the client story summary (section 18). */
export const clientStoryClauseSchema = z.object({
    field: z.string(),
    sentence: z.string(),
});

export const clientStorySummarySchema = z.object({
    clauses: z.array(clientStoryClauseSchema),
    text: z.string(),
    missingFields: z.array(z.string()),
});

/** Everything the Internal Review pulls through, on every open. */
export const suitabilityPullThroughSchema = z.object({
    factFindSource: factFindSourceSchema.nullable(),
    factFindRisk: factFindRiskSummarySchema.nullable(),
    enquiry: suitabilityEnquiryIntentSchema,
    /** The `investment_objective` answer of the fact find. */
    factFindObjective: z.string().nullable(),
    /**
     * The service type on the CLIENT RECORD, which is what the firm does today.
     *
     * It is a fact, and never a recommendation. The prefill deliberately leaves
     * `serviceRecommendation` blank, because a recommendation is a conclusion of
     * the IM and it must carry a written reason (SEP-257 items 19 to 21). The
     * panel prints this value so the IM sees the standing arrangement.
     */
    clientServiceType: z.string().nullable(),
    restrictions: suitabilityRestrictionsSchema,
    experience: z.array(investmentExperienceEntrySchema),
    knowledge: factFindKnowledgeSchema,
    /** The scored Knowledge and Experience breakdown of the same answers. */
    knowledgeAndExperience: knowledgeExperienceScoreSchema,
    classification: suitabilityClassificationPullThroughSchema,
    vulnerability: suitabilityVulnerabilitySchema,
    salesDocuments: z.array(suitabilitySalesDocumentSchema),
    /**
     * Where the provided register stands, including the mandatory items it does
     * not carry (section 27). An empty register therefore never reads as "the
     * firm gave the client everything".
     */
    salesDocumentSummary: suitabilitySalesDocumentSummarySchema,
    /**
     * Which of the six declarations the data supports, and with what evidence
     * (sections 28 and 29). A declaration the data cannot support stays a plain
     * confirmation by the IM.
     */
    declarationSupport: z.array(suitabilityDeclarationSupportSchema),
    corrections: z.array(suitabilityClientCorrectionSchema),
    /**
     * The saved fields that still hold the answer BEFORE a correction. The list
     * is empty while the review holds no saved value that disagrees.
     */
    supersededFields: z.array(suitabilitySupersededFieldSchema),
    flags: z.array(suitabilityFlagSchema),
    riskRationale: suitabilityRiskRationaleSchema,
    story: clientStorySummarySchema,
    /** The facts the firm holds for the form fields, with their sources (SEP-325). */
    knownFacts: z.array(suitabilityKnownFactSchema),
    /** Which groups the persona of the reader withholds. */
    withheld: suitabilityWithheldSchema,
});
export type SuitabilityPullThroughDto = z.infer<typeof suitabilityPullThroughSchema>;

// The GET response: the current assessment (`exists: true`) OR a prefilled
// blank draft (`exists: false`) seeded from the Client + Fact Find. `clientName`
// is always present for the page header.
export const suitabilityAssessmentGetResponseSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    exists: z.boolean(),
    // The current saved assessment when `exists`, else null.
    assessment: suitabilityAssessmentDtoSchema.nullable(),
    // The prefilled `fields` block. It travels on EVERY open (SEP-243 section
    // 17), and not only when no assessment exists: a client correction that
    // lands after the review opened must reach the open review. The form fills
    // a BLANK field from it, and it never overwrites a value the IM entered.
    // Sourced from real Client, Fact Find and ValuationGrid data only
    // (ADR-0009). A field with no source stays null.
    prefill: suitabilityAssessmentFieldsSchema,
    // Suggested form variant for a fresh assessment, derived from the Client's
    // legal-entity type. (When `exists`, read `assessment.clientType` instead.)
    suggestedClientType: clientTypeSchema,
    // The read-only pull-through block (SEP-243 sections 12 and 17 to 25). It
    // travels on EVERY open, so a correction recorded after the review opened
    // reaches the open review.
    pullThrough: suitabilityPullThroughSchema,
    // The append-only register of overrides of a derived figure (SEP-243
    // section 26), newest first, INCLUDING the withdrawn rows. The peer
    // reviewer and Compliance both read this route, so both see every override
    // and the figure it replaced.
    calculationOverrides: z.array(suitabilityCalculationOverrideSchema),
    // The signed-off versions of the review, newest first (SEP-243 sections 30,
    // 33 and 35). Version 1 is never overwritten, and the list proves it.
    versions: z.array(suitabilityAssessmentVersionSchema),
    // Which version the working draft becomes. `suitabilityWorkingVersionNumber`
    // computes it, and no column holds it.
    workingVersionNumber: z.number().int(),
});
export type SuitabilityAssessmentGetResponse = z.infer<
    typeof suitabilityAssessmentGetResponseSchema
>;

// --- ATLAS document-extraction prefill suggestions (DESK5 4c) ---
// Staged, IM-reviewable field values extracted from an uploaded suitability
// document. The anchor is a single raw `fieldKey` (one of the suitability
// form-field ids); accepting one dispatches SaveSuitabilityAssessment with
// `fields:{[fieldKey]:value}`.
export const suitabilityPrefillReviewStatusSchema = z.enum(['pending', 'accepted', 'rejected']);
export type SuitabilityPrefillReviewStatus = z.infer<typeof suitabilityPrefillReviewStatusSchema>;

export const suitabilityPrefillSuggestionSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    assessmentId: z.string().nullable(),
    fieldKey: z.string(),
    suggestedValue: z.union([z.string(), z.number(), z.boolean(), z.null()]).nullable(),
    source: z.literal('atlas_document'),
    sourceUploadId: z.string().nullable(),
    sourceQuote: z.string().nullable(),
    reviewStatus: suitabilityPrefillReviewStatusSchema,
    reviewedBy: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    confidence: z.enum(['high', 'medium', 'low']),
    createdAt: z.string(),
});
export type SuitabilityPrefillSuggestion = z.infer<typeof suitabilityPrefillSuggestionSchema>;

export const suitabilityPrefillListResponseSchema = z.object({
    suggestions: z.array(suitabilityPrefillSuggestionSchema),
});
export const suitabilityPrefillActionResponseSchema = z.object({
    suggestion: suitabilityPrefillSuggestionSchema,
});

export const suitabilityAssessmentContract = c.router(
    {
        getSuitabilityAssessment: {
            method: 'GET',
            path: '/desk/suitability/assessment/:clientId',
            pathParams: z.object({ clientId: z.string() }),
            responses: {
                200: suitabilityAssessmentGetResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "A client's Internal Suitability Assessment (or a prefilled blank draft)",
        },
        saveSuitabilityAssessment: {
            method: 'POST',
            path: '/desk/suitability/assessment/save',
            body: z.object({
                // Absent on first save; present to patch an existing draft.
                id: z.string().optional(),
                clientId: z.string().min(1),
                // Set on create (defaults to `individual`); a patch may omit it.
                clientType: clientTypeSchema.optional(),
                fields: suitabilityAssessmentFieldsSchema.partial(),
            }),
            responses: {
                200: z.object({ assessment: suitabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Create or patch the working draft of a suitability assessment',
        },
        // -- The sign-off and the amendment round (SEP-243 sections 30, 33
        // and 35) --
        //
        // The sign-off LOCKS the content into a version, and only the
        // responsible investment manager may call it. The retired
        // `completeSuitabilityAssessment` admitted `view` and `im-assistant`,
        // so leadership and the EA could both sign a review off.
        signOffSuitabilityAssessment: {
            method: 'POST',
            path: '/desk/suitability/assessment/:id/sign-off',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({
                    assessment: suitabilityAssessmentDtoSchema,
                    version: suitabilityAssessmentVersionSchema,
                }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'The responsible IM signs off the Internal Review, and the version locks',
        },
        openSuitabilityAmendmentRound: {
            method: 'POST',
            path: '/desk/suitability/assessment/:id/amendment-round',
            pathParams: z.object({ id: z.string() }),
            // The reason is mandatory. A bare unlock would move a signed-off
            // review with nothing on the file to say why.
            body: z.object({ reason: z.string().min(1) }),
            responses: {
                200: z.object({ assessment: suitabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Reopen a signed-off Internal Review as the next version',
        },
        // -- The overrides of a derived figure (SEP-243 section 26) --
        //
        // THE BODY CARRIES NO CALCULATED VALUE. The Action derives it from the
        // stored raw inputs of the assessment, so no caller can post a figure
        // the platform did not compute. `reason` is mandatory. The wire refuses
        // a reason of whitespace alone with a 400, and the Action refuses it
        // again at every other door.
        recordCalculationOverride: {
            method: 'POST',
            path: '/desk/suitability/assessment/:id/override',
            pathParams: z.object({ id: z.string() }),
            body: z.object({
                calculationKey: suitabilityCalculationKeySchema,
                overrideValue: z.number(),
                reason: z.string().trim().min(1),
            }),
            responses: {
                200: z.object({ override: suitabilityCalculationOverrideSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Override one derived figure of an assessment, with a mandatory rationale',
        },
        withdrawCalculationOverride: {
            method: 'POST',
            path: '/desk/suitability/assessment/override/:overrideId/withdraw',
            pathParams: z.object({ overrideId: z.string() }),
            body: z.object({ reason: z.string().min(1) }),
            responses: {
                200: z.object({ override: suitabilityCalculationOverrideSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Withdraw an override. The row stays, and it takes a withdrawn status.',
        },
        // -- ATLAS document-extraction prefill workbench (DESK5 4c) --
        listPrefill: {
            method: 'GET',
            path: '/desk/suitability/assessment/:clientId/prefill',
            pathParams: z.object({ clientId: z.string() }),
            responses: { 200: suitabilityPrefillListResponseSchema, 403: errorBody },
            summary: 'List ATLAS suitability pre-fill suggestions for a client',
        },
        acceptPrefill: {
            method: 'POST',
            path: '/desk/suitability/assessment/prefill/:suggestionId/accept',
            pathParams: z.object({ suggestionId: z.string() }),
            body: z.object({ by: z.string() }),
            responses: {
                200: suitabilityPrefillActionResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Accept a suitability pre-fill suggestion and write it into the assessment',
        },
        rejectPrefill: {
            method: 'POST',
            path: '/desk/suitability/assessment/prefill/:suggestionId/reject',
            pathParams: z.object({ suggestionId: z.string() }),
            body: z.object({ by: z.string() }),
            responses: {
                200: suitabilityPrefillActionResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Reject a suitability pre-fill suggestion',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
